import './index.css'
import cn from 'clsx'

import React, { useCallback, useEffect, useState } from 'react'
import Collapsible from 'components/ui/collapsible'
import Icon from 'components/ui/icon'

type Props = {
  children: React.ReactNode
  expanded?: boolean
  icon?: React.ReactNode
  iconSize?: number
  title: React.ReactNode
  onTitleClick?: (open: boolean) => void
  titleClassName?: string
  contentClassName?: string
  arrowType?: 'arrow-right' | 'arrow-down'
  scrollIntoView?: boolean
  adjustedContentHeight?: string | number
}

/**
 * @deprecated Consider using Radix `Accordion` from `@radix-ui/themes` or create a DS wrapper. Gradual migration recommended.
 * @see https://www.radix-ui.com/themes/docs/components/accordion
 * @migration
 * - Replace with Radix Accordion compound component:
 *   ```tsx
 *   // Legacy
 *   <Accordion title="Section 1" expanded={true}>
 *     Content here
 *   </Accordion>
 *
 *   // Radix alternative
 *   import * as Accordion from '@radix-ui/react-accordion'
 *
 *   <Accordion.Root type="single" defaultValue="item-1" collapsible>
 *     <Accordion.Item value="item-1">
 *       <Accordion.Header>
 *         <Accordion.Trigger>Section 1</Accordion.Trigger>
 *       </Accordion.Header>
 *       <Accordion.Content>Content here</Accordion.Content>
 *     </Accordion.Item>
 *   </Accordion.Root>
 *   ```
 * - Prop mapping:
 *   - title → `<Accordion.Trigger>`
 *   - children → `<Accordion.Content>`
 *   - expanded → `defaultValue` on Accordion.Root (use value prop for controlled)
 *   - onTitleClick → `onValueChange` on Accordion.Root
 * - Custom features requiring additional implementation:
 *   - iconSize/arrowType → Style via CSS or custom trigger component
 *   - scrollIntoView → Implement with custom scroll logic
 *   - adjustedContentHeight → Use CSS or Accordion.Content styling
 * - Note: Radix Accordion is not currently exported from components/ds; import directly from @radix-ui/react-accordion
 *
 * A collapsible accordion component with customizable title and content
 *
 * @param children - The content to display when the accordion is expanded
 * @param expanded - Whether the accordion should be expanded by default (default: false)
 * @param iconSize - Size of the arrow icon in pixels (default: 24)
 * @param title - The title/header content displayed in the accordion
 * @param titleClassName - CSS class name for the title element
 * @param contentClassName - CSS class name for the content element
 * @param arrowType - Type of arrow icon to display (default: 'arrow-right')
 * @param onTitleClick - Callback function called when the title is clicked
 * @param scrollIntoView - Whether to scroll the element into view when expanded (default: true)
 * @param adjustedContentHeight - Custom height for the content area
 * @returns The accordion component
 */
const Accordion = ({
  children,
  expanded = false,
  iconSize = 24,
  title,
  titleClassName,
  contentClassName,
  arrowType = 'arrow-right',
  onTitleClick,
  scrollIntoView = true,
  adjustedContentHeight,
}: Props): React.ReactNode => {
  const [open, setOpen] = useState<boolean>(expanded)
  const handleTitleClick = useCallback(() => {
    setOpen((open: boolean) => {
      if (onTitleClick) {
        onTitleClick(!open)
      }

      return !open
    })
  }, [onTitleClick])
  useEffect(() => {
    setOpen(expanded)
  }, [expanded])

  return (
    <>
      <div
        className={cn('Accordion_Title', titleClassName)}
        onClick={handleTitleClick}
      >
        {title}
        <div
          className={cn('Accordion_Icon', `Accordion_Icon--${arrowType}`, {
            'Accordion_Icon--Open': open,
          })}
        >
          <Icon type={arrowType} width={iconSize} height={iconSize} />
        </div>
      </div>
      <Collapsible
        open={open}
        scrollIntoView={scrollIntoView}
        adjustedContentHeight={adjustedContentHeight}
      >
        <div className={cn('Accordion_Content', contentClassName)}>
          {children}
        </div>
      </Collapsible>
    </>
  )
}

export default Accordion
